'use client'

import { CustomDataPage } from '@repo/custom-data-pages/CustomDataPage'
import { CustomDataPageMenu } from '@repo/custom-data-pages/CustomDataPageMenu'
import { CustomDataSearchBox } from '@repo/custom-data-pages/CustomDataSearchBox'
import { EmptyState } from '@repo/ui/EmptyState'
import { LargeCustomDataPageIcon } from '@repo/ui/LargeCustomDataPageIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useState, useEffect, useRef } from 'react'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

const CustomDataPages = ({ testMode = false, type = 'matter' }) => {
  const { currentMatter: matter } = useMattersStore()
  const { fetchCustomDataPagesWithFields } = useCustomDataPageStore()
  const [isFetching, setIsFetching] = useState(true)
  const [pages, setPages] = useState([])
  const [searchMenuItems, setSearchMenuItems] = useState([])
  const containerRef = useRef(null)
  const pagesRef = useRef(null)
  const menuRef = useRef(null)
  const { height: windowHeight } = useWindowSize()

  useEffect(() => {
    if (testMode) return

    const fetchData = async () => {
      let workflowTypeId

      if (type === 'matter') workflowTypeId = matter?.workflowTypeId || null
      if (type === 'client') workflowTypeId = 'GLOBAL'

      if (!workflowTypeId) return

      setIsFetching(true)

      try {
        const pagesByWorkType = (await fetchCustomDataPagesWithFields({ workflowTypeId })) || []
        const pages = pagesByWorkType.map((page, index) => ({
          ...page,
          isOpen: index === 0
        }))

        setPages(pages)
      } catch (err) {
        if (err.status === 404) console.log('oh no 404')
        else console.error(err)
      } finally {
        setIsFetching(false)
      }

      setIsFetching(false)
    }

    fetchData()
  }, [testMode, matter, fetchCustomDataPagesWithFields, type])

  useEffect(() => {
    if (testMode || !pages?.length) return

    const searchMenuItems = []

    pages.forEach((page) => {
      page.fields?.forEach((field) => {
        const fieldId = field.customDataFieldId || field.fieldId
        searchMenuItems.push({
          page,
          field,
          value: `${page.customDataPageId}-${fieldId}`,
          label: `${page.description} - ${field.description}`
        })
      })
    })

    setSearchMenuItems(searchMenuItems)
  }, [testMode, pages])

  const setPageIsOpen = ({ pageId, isOpen }) => {
    const updatedPages = pages.map((page) => {
      if (page.customDataPageId === pageId) {
        return { ...page, isOpen }
      }

      return page
    })

    setPages(updatedPages)
  }

  const handlePageClick = (params) => {
    const { page } = params || {}
    if (!page) return
    setPageIsOpen({ pageId: page.customDataPageId, isOpen: !page.isOpen })
    scrollToItem({ type: 'page', pageId: page.customDataPageId })
  }

  const handleFieldClick = (params) => {
    const { page, field } = params || {}
    if (!page || !field) return

    const scrollParams = {
      type: 'field',
      pageId: page.customDataPageId,
      fieldId: field.customDataFieldId || field.fieldId
    }

    if (page.isOpen) {
      scrollToItem(scrollParams)
    } else {
      setPageIsOpen({ pageId: page.customDataPageId, isOpen: true })
      // wait for the page to open
      setTimeout(() => scrollToItem(scrollParams), 1200)
    }
  }

  const scrollToItem = ({ type, pageId, fieldId }) => {
    const pagesId = type === 'page' ? `#custom-data-page-${pageId}` : `#custom-data-field-${pageId}-${fieldId}`
    const pagesElement = document.querySelector(pagesId)
    const pagesOffsetTop = pagesElement?.offsetTop || 0
    pagesRef.current.scrollTo({ top: pagesOffsetTop, left: 0, behavior: 'smooth' })

    const menuId = type === 'page' ? `#custom-data-page-menu-${pageId}` : `#custom-data-field-menu-${pageId}-${fieldId}`
    const menuElement = document.querySelector(menuId)
    const menuOffsetTop = menuElement?.offsetTop || 0
    menuRef.current.scrollTo({ top: menuOffsetTop, left: 0, behavior: 'smooth' })
  }

  const offsetTop = containerRef?.current?.offsetTop || 0
  const height = `calc(${windowHeight || 0}px - ${offsetTop}px)`

  const sx = {
    width: '100%',
    maxWidth: '1600px',
    margin: '30px auto 0',
    padding: '0 40px',
    height
  }

  const worktypeDescription = matter?.workflowTypeDescription || 'GLOBAL'

  if (!matter)
    return (
      <MUIStack data-testid="CustomDataPages" sx={sx}>
        Please select a matter
      </MUIStack>
    )

  if (!pages?.length && isFetching)
    return (
      <MUIStack data-testid="CustomDataPages" sx={sx}>
        <LoadingState />
      </MUIStack>
    )

  if (!pages?.length && !isFetching)
    return (
      <MUIStack data-testid="CustomDataPages" sx={{ ...sx, height: '400px', maxWidth: '460px', textAlign: 'center' }}>
        <EmptyState
          heading="No Custom Data Pages to show"
          text={`Check that there are Custom Data Pages linked to the worktype: '${worktypeDescription}', and that those pages have fields linked to them`}
          icon={<LargeCustomDataPageIcon sx={{ mb: 8, width: '64px', height: '64px' }} />}
        />
      </MUIStack>
    )

  return (
    <>
      <MUIStack direction="row" justifyContent="center" data-testid="MatterData" gap="0px" sx={sx} ref={containerRef}>
        <MUIStack direction="column" gap="20px" sx={{ width: '350px' }}>
          <CustomDataSearchBox onChange={handleFieldClick} options={searchMenuItems} />
          <CustomDataPageMenu
            pages={pages}
            handlePageClick={handlePageClick}
            handleFieldClick={handleFieldClick}
            setPageIsOpen={setPageIsOpen}
            scrollRef={menuRef}
          />
        </MUIStack>

        <MUIStack
          ref={pagesRef}
          direction="column"
          gap="30px"
          sx={{ overflowY: 'auto', position: 'relative', flex: 1 }}
        >
          {pages.map((page) => (
            <CustomDataPage
              key={page.customDataPageId}
              clientNo={matter.clientNo}
              matterNo={type === 'matter' ? Number(matter.matterNo) : 0}
              pageId={page.customDataPageId}
              pageTitle={page.description}
              fields={page.fields}
              isOpen={page.isOpen}
              setPageIsOpen={setPageIsOpen}
              allowMultipleEntries={page.allowMultipleEntries}
            />
          ))}
        </MUIStack>
      </MUIStack>
    </>
  )
}

CustomDataPages.propTypes = {
  testMode: PropTypes.bool,
  type: PropTypes.string
}

export default CustomDataPages
